import { formatError } from '@shared/utils/RequestUtils';

export interface SmartRetryOptions {
    /** Máximo número de reintentos. Por defecto: 2 */
    maxRetries?: number;
    /** Códigos de estado específicos en los que NO hacer retry */
    skipRetryCodes?: number[];
    /** Callback para logging personalizado */
    onRetryEvaluation?: (decision: RetryDecision) => void;
}

export interface RetryDecision {
    shouldRetry: boolean;
    reason: string;
    failureCount: number;
    statusCode?: number;
    message: string;
}

/**
 * Hook que proporciona lógica de retry inteligente para react-query
 * Usa el FormattedError simplificado para determinar si hacer retry
 */
export const useSmartRetry = (options: SmartRetryOptions = {}) => {
    const {
        maxRetries = 2,
        skipRetryCodes = [],
        onRetryEvaluation
    } = options;

    const retryFunction = (failureCount: number, error: any): boolean => {
        // Analizar el error - ahora más simple
        const formattedError = formatError(error);
        const { statusCode, shouldRetry: errorSuggestsRetry, message } = formattedError;

        let shouldRetry = false;
        let reason = '';

        // Verificar si está en la lista de códigos a omitir
        if (statusCode && skipRetryCodes.includes(statusCode)) {
            shouldRetry = false;
            reason = `Código ${statusCode} en lista de omisión`;
        }
        // Verificar máximo de reintentos
        else if (failureCount >= maxRetries) {
            shouldRetry = false;
            reason = `Máximo de intentos alcanzado (${maxRetries})`;
        }
        // Usar la sugerencia del error formateado
        else if (errorSuggestsRetry) {
            shouldRetry = true;
            reason = 'Error de servidor o red - retry recomendado';
        }
        else {
            shouldRetry = false;
            reason = 'Error de cliente - no retry';
        }

        const decision: RetryDecision = {
            shouldRetry,
            reason,
            failureCount,
            statusCode,
            message
        };

        // Callback personalizado si se proporciona
        onRetryEvaluation?.(decision);

        return shouldRetry;
    };

    return retryFunction;
}; 